/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import { decodeResponseValue, MultiSelectEditor, generateOptions, responseRenderer } from '../../../utils/responseColumn';
import Handsontable from 'handsontable';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.min.css';
import './save-indicators.css'
import {
  AGREE_DISAGREE_COL,
  AUDIT_COLUMN_HEADER,
  AUDIT_SHEET_HEADER,
  AUDIT_SHEET_KEY,
  DOLLAR_COL,
  NUMERIC_KEYS,
  RENDERING_AS_COL,
  RESPONSE_AS_COL,
} from '../constants';
import { registerAllModules } from 'handsontable/registry';
import '../styles.css';
import { useContext } from 'react';
import { AuditSheetContext } from '../../../providers/AuditSheetProvider';
import { useInterval } from '../../../hooks';
import { useToastr } from '@laxmimanogna/code-quick-components';
import { getRowColArray, getRowColIndex } from '../getRowColumn';
import AppColors from '../../../constants/AppColors';
import { registerCustomCells } from './hands_on_table_renderers/CustomRenderers';
import { format } from 'date-fns';
import { toTitleCase } from '../../../utils/common.utils';
import {
  creatColumnHeader,
  createColumnDataName,
  emptyDataPrefilledObject,
  findAgreeDisAgreeColumn,
  findDollorColumn,
  findHighlightedColumn,
  findNumericColumns,
  findResponseColumnIndex,
  getAuditSheetKeys,
  isColumnMatched,
  isEmptySheet,
  prepareAuditSheetColumn,
  prepareAuditSheetHeader,
  validateUnits,
  validateUnitsList,
  handleAgreeDisagree,
  updateRVUand$Value,
  getBaseKeyword,
  validateCPT,
  validateCPTList,
  validateICDList,
  cptSanitize,
  validateColumnValue,
  hasValueChanged,
  createDynamicColumnKeyToCountMap,
} from '../../../utils/auditsheet.utils';
import { dynamicColumnKeywords } from '../../../constants/constants';
import { removeNullObjects, sanitize } from './sanitize';
import useIdleAutoSave from '../hooks/useIdleAutoSave';
import { acknowledgeChanges, hasPendingChanges, isRetryableSheetSaveError, queueCreatedRow, snapshotChanges } from '../pendingSheetChanges';

// Register the editor
Handsontable.editors.registerEditor('multi-select', MultiSelectEditor);

registerCustomCells({});

function getScaledRowHeight(scale) {
  return `${24 * scale}px`;
}

function getScaledHeaderFontSize(scale) {
  return 13 * scale + 'px';
}

function getScaledRowFontSize(scale) {
  return 14 * scale + 'px';
}

//  -------------------component starts here-----------------------------

const AuditSheet = ({
  scale = 1,
  sheet,
  uploadId,
  view,
  updateAuditSheet,
  updateSheet,
  onCommentClick,
  onSelectionEnd,
  closeComment,
  height,
  removeRows,
  getSheet,
  batch
}) => {
  // refs
  const hotRef = useRef(null);

  // props

  /**
   *  { updateSheet } update the data in setState(setSheet);
   *  { updateAuditSheet } create audit sheet(POST) and getSheets and update sheet;
   *  { sheet } gives a current sheet not sheets
   */

  // context
  const auditSheetContext = useContext(AuditSheetContext);

  const { industryCodes, iCDCodes, modifiers, getComments, currentUpload } = auditSheetContext;
  const { commentFlags, providerOptions } = auditSheetContext;
  const { responseAuditIssues } = auditSheetContext;
  const responseAuditIssuesRef = useRef(responseAuditIssues);

  //  common states
  const { highlightedColumn, setHilightedColumn } = auditSheetContext;
  const { dollarColumn, setDollorColumn } = auditSheetContext;
  const { auditSheetColumnsKeys, setAuditSheetColumnsKeys } = auditSheetContext;
  const { agreeDisAgreeCol, setAgreeDisAgreeCol } = auditSheetContext;
  const { setSelectedRowCol, setAuditsheetReference } = auditSheetContext

  //  common boleans
  const { isAuditedDollarDisabled, iaddState } = auditSheetContext;
  const { isProviderDollarDisabled, ipddState } = auditSheetContext;

  const [columnData, setColumnData] = useState([]);
  const [auditSheetHeaders, setAuditSheetHeaders] = useState([]);

  function getWindowSize() {
    const { innerWidth, innerHeight } = window;
    return { innerWidth, innerHeight };
  }

  // states
  const [sheetData, setSheetData] = useState([]);
  const [windowSize, setWindowSize] = useState(getWindowSize().innerWidth);
  const [localHeader, setLocalHeader] = useState(auditSheetHeaders);
  const [localColumnData, setLocalColumnData] = useState(columnData);
  const [numericColumns, setNumericColumns] = useState([]);
  const [responseColumn, setResponseColumn] = useState(null);

  // other hooks
  const toast = useToastr();

  // Queues row-level changes locally before sending to server
  const [pendingChanges, setPendingChanges] = useState({
    created: new Set(),
    updated: new Map(),
    deleted: new Set(),
  });
  const [pendingNewRows, setPendingNewRows] = useState(new Set());
  // Tracks whether a save is currently in progress
  const isSavingRef = useRef(false);
  const currentEditingRowRef = useRef(null);

  // Keeps pending changes accessible inside callbacks without causing re-renders
  const pendingChangesRef = useRef(pendingChanges);
  const sheetRef = useRef(sheet);

  useEffect(() => {
    pendingChangesRef.current = pendingChanges;
  }, [pendingChanges]);

  useEffect(() => {
    sheetRef.current = sheet;
  }, [sheet]);

  const ICD_CODES = useMemo(() => new Map(Object.keys(iCDCodes ?? {}).flatMap(code => [
    [code, code],
    [code.replaceAll(".", ""), code]
  ])
  ), [iCDCodes])

  const INDUSTRY_CODES = useMemo(() => new Set(Object.keys(industryCodes ?? {})), [industryCodes])

  // Queues a single row edit for the next save
  const trackChange = (rowData) => {
    if (!rowData) return;

    const newChanges = {
      created: new Set(pendingChangesRef.current.created),
      updated: new Map(pendingChangesRef.current.updated),
      deleted: new Set(pendingChangesRef.current.deleted),
    };
    if (!rowData.id || rowData.id === null) {
      queueCreatedRow(newChanges.created, rowData);
    } else {
      newChanges.updated.set(rowData.id, rowData);
    }
    pendingChangesRef.current = newChanges; // sync ref immediately
    setPendingChanges(newChanges);          // sync state for UI render
    resetIdleTimer();
  };

  // Queues deleted row IDs for the next save
  const trackDeletion = (rowIds) => {
    const newChanges = {
      created: new Set(pendingChangesRef.current.created),
      updated: new Map(pendingChangesRef.current.updated),
      deleted: new Set(pendingChangesRef.current.deleted),
    };
    
    rowIds.forEach(id => {
      if (id && id !== null) {
        newChanges.deleted.add(id);
        newChanges.updated.delete(id);
      }
    });

    pendingChangesRef.current = newChanges; // sync ref immediately
    setPendingChanges(newChanges);          // sync state for UI
  };

  // Acknowledge only the snapshot the server accepted. Newer edits remain queued.
  const performSave = useCallback(async () => {
    if (view || !hasPendingChanges(pendingChangesRef.current)) return true;
    if (isSavingRef.current) return false;
    isSavingRef.current = true;
    hotRef.current?.hotInstance?.render();
    const changesToSave = snapshotChanges(pendingChangesRef.current);
    const currentSheet = sheetRef.current;
    let retry = false;
    try {
      const response = await updateAuditSheet({
        ...currentSheet,
        deltaPayload: {
          sheet_name: currentSheet.sheet_name,
          index: currentSheet.index,
          ...changesToSave,
          save_id: Date.now(),
        },
      });
      if (!response) {
        // Another save has the container lock. Keep this snapshot queued.
        retry = true;
        return false;
      }
      const liveRows = sheetRef.current.data;
      const remaining = acknowledgeChanges(pendingChangesRef.current, changesToSave, response, liveRows);
      pendingChangesRef.current = remaining;
      setPendingChanges(remaining);
      updateSheet([...liveRows]);
      setPendingNewRows(previous => new Set([...previous].filter(index => !liveRows[index]?.id)));
      retry = hasPendingChanges(remaining);
      return true;
    } catch (error) {
      retry = isRetryableSheetSaveError(error);
      if (error?.name !== 'API_ERROR') toast.showError({ description: 'Changes were not saved. Your edits are still on this page.' });
      return false;
    } finally {
      isSavingRef.current = false;
      if (retry) resetIdleTimer();
      else cancelIdleTimer();
      hotRef.current?.hotInstance?.render();
    }
  }, [updateAuditSheet, updateSheet, view, toast]);

  const { resetIdleTimer, cancelIdleTimer } = useIdleAutoSave({
    pendingChangesRef,
    performSave,
    view,
    isAuditFormOpen: auditSheetContext.isAuditFormOpen,
  });

  const debouncedSave = useCallback(() => {
    performSave();
  }, [performSave]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const currentPending = pendingChangesRef.current;
      const hasChanges = currentPending.created.size > 0 || 
                        currentPending.updated.size > 0 || 
                        currentPending.deleted.size > 0;
      
      if (hasChanges && !view) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Triggers a save when user moves to a different row
  const handleRowChange = (newRowIndex, isMovingRows = false) => {
    const previousRowIndex = currentEditingRowRef.current;
    
    if (isMovingRows && previousRowIndex !== null && previousRowIndex !== newRowIndex) {
      if (!view && !auditSheetContext.isAuditFormOpen) {
        debouncedSave();
      }
    }
    
    currentEditingRowRef.current = newRowIndex;
  };

  registerAllModules(); // importing all modules in compoenent level

  // formatting date and assigning the renderer name ( object -> name )
  function setLocalSheetData(data) {
    sheetRef.current = { ...sheetRef.current, data: data || [] };
    if (!data) {
      setSheetData([]);
    }

    const nextSheetData = [];

    for (let i = 0; i < data.length; i++) {
      const datum = data[i];

      const renderingValue = datum.rendering;

      if (renderingValue) {
        const isObject =
          typeof renderingValue === 'object' &&
          Object.keys(renderingValue).length;

        if (isObject) {
          datum.rendering = renderingValue.full_name;
          datum.old_rendering = renderingValue;
        } else {
          const providerOption = providerOptions.find(
            p => p.id === renderingValue
          );

          if (providerOption) {
            datum.rendering = providerOption.value;
          }
        }
      }

      if (datum.enc_dt) {
        const d = Date.parse(datum.enc_dt);
        if (d) {
          datum.enc_dt = format(new Date(d), 'MM-dd-yyyy');
        }
      }
      nextSheetData.push(datum);
    }

    //  des-object and set to setSheetData(local)
    setSheetData(nextSheetData);
  }


  React.useEffect(() => {
    setColumnData(localColumnData);
    setAuditSheetHeaders(localHeader);
  }, [localHeader, localColumnData]);

  // lifeCycle Hooks
  React.useEffect(() => {
    if (hotRef?.current) {
      // Store only the current reference
      setAuditsheetReference(prev => ({ ...prev, hotRef: hotRef.current }));
    }

    const reDestructuredData = sheet.data;

    // spreading in object
    const _reDestructuredData = reDestructuredData.map(sd => {
      const tempObj = { ...sd, ...sd?.additional_attributes };

      delete tempObj.additional_attributes;

      return tempObj;
    });

    const columnMapping = createDynamicColumnKeyToCountMap(_reDestructuredData)
    // -- preparing dynamic column headers --
    let auditSheetColumnsheaders = prepareAuditSheetHeader(
      _reDestructuredData, 
      columnMapping,
      batch
    );
    setAuditSheetHeaders(auditSheetColumnsheaders);

    // -- preparing dynamic auditsheetcolummn --
    const dynamicColumnData = prepareAuditSheetColumn(
      _reDestructuredData,
      windowSize,
      view,
      providerOptions,
      isAuditedDollarDisabled,
      isProviderDollarDisabled,
      currentUpload?.provider?.full_name,
      columnMapping,
      batch
    );

    const responseColumnIndex = dynamicColumnData.findIndex(
      col => col.data === 'response'
    );

    if (responseColumnIndex !== -1) {
      dynamicColumnData[responseColumnIndex].source = function () {
        const rowData = this.instance.getSourceDataAtRow(this.row);
        return generateOptions(rowData, responseAuditIssuesRef.current);
      };

      dynamicColumnData[responseColumnIndex].renderer = function (
        instance,
        td,
        row,
        col,
        prop,
        value,
        cellProperties
      ) {
        return responseRenderer(instance, td, row, col, prop, value, cellProperties, responseAuditIssuesRef.current);
      };
    }

    if (!!dynamicColumnData.length) {
      setColumnData(dynamicColumnData);
    }
  }, [sheet?.data, responseAuditIssues]);

  useEffect(() => {
    responseAuditIssuesRef.current = responseAuditIssues;
    if (responseAuditIssues?.length && hotRef.current?.hotInstance) {
      hotRef.current.hotInstance.render();
    }
  }, [responseAuditIssues]);

  useEffect(() => {
    if (hotRef.current?.hotInstance) {
      hotRef.current.hotInstance.render();
    }
  }, [commentFlags]);

  React.useEffect(() => {
    const _hilghtedColumn = findHighlightedColumn(columnData);
    const _dollorColumn = findDollorColumn(columnData);
    const _auditSheetkey = getAuditSheetKeys(columnData);
    const _agreeDisagree = findAgreeDisAgreeColumn(columnData);
    const _numericColumn = findNumericColumns(columnData);
    const _responseColumn = findResponseColumnIndex(columnData);

    setResponseColumn(_responseColumn);
    setAuditSheetColumnsKeys([..._auditSheetkey]);
    setHilightedColumn([..._hilghtedColumn]);
    setDollorColumn([..._dollorColumn]);
    setAgreeDisAgreeCol([..._agreeDisagree]);
    setNumericColumns(_numericColumn);
  }, [columnData.length]);

  useEffect(() => {
    registerCustomCells({ scale });
  }, [scale]);

  useEffect(() => {
    // when sheet data changes it will destructure rendering , enc_dt values update in sheetdata(state)
    if (sheet?.data) {
      setLocalSheetData(sheet.data);
    }
    // sheet is empty , remove additional columns

    const isNewSheet = isEmptySheet(sheet.data);

    const preFilledObject = emptyDataPrefilledObject(currentUpload);

    if (isNewSheet && Object.keys(currentUpload).length) {
      const preFilledEmptyObject = sheet.data.map((s, i) => {
        if (i === 0) {
          return preFilledObject;
        } else {
          return s;
        }
      });
      updateSheet(preFilledEmptyObject);

      const auditSheetColumnsheaders = prepareAuditSheetHeader(preFilledEmptyObject, {}, batch);
      setAuditSheetHeaders(auditSheetColumnsheaders);

      //  -- preparing dynamic auditsheetcolummn --
      const dynamicColumnData = prepareAuditSheetColumn(
        preFilledEmptyObject,
        windowSize,
        view,
        providerOptions,
        isAuditedDollarDisabled,
        isProviderDollarDisabled,
        currentUpload?.provider?.full_name,
        {},
        batch
      );

      if (!!dynamicColumnData.length) {
        setColumnData(dynamicColumnData);
      }
    }
  }, [sheet?.data, currentUpload]);

  useEffect(() => {
    const hot = hotRef.current.hotInstance;

    if (
      Object.keys(auditSheetContext.selectedCell).length &&
      sheetData.length
    ) {
      const { row, col } = auditSheetContext.selectedCell;
      const index = sheetData.findIndex(d => d.id === row);
      if (index !== -1) {
        hot.selectCell(index, col);
      }
    } else {
      hot.deselectCell();
    }
  }, [auditSheetContext.selectedCell]);

  function columnHighlight(
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties
  ) {
    Handsontable.renderers.TextRenderer.apply(this, arguments);
    td.style.background = AppColors.lightPink;
    td.style.fontSize = getScaledRowFontSize(scale);
    td.style.textAlign = 'center';
  }

  function commentCell(instance, td, row, col, prop, value, cellProperties) {
    Handsontable.renderers.TextRenderer.apply(this, arguments);
    td.className = `htCommentCell`;
    td.style.fontSize = getScaledRowFontSize(scale);
    td.style.textAlign = col !== RENDERING_AS_COL && 'center';
    const COL = highlightedColumn; // [6, 7, 15, 16];

    if (COL.includes(col)) {
      td.style.background = AppColors.lightPink;
    }

    if (batch) {
      td.style.textAlign = 'center';
      if (agreeDisAgreeCol.includes(col)) {
        //agree
        td.innerHTML = value === true ? 'Yes' : '-';
      }
    } else {
      td.style.textAlign = col !== RENDERING_AS_COL && 'center';

      if (agreeDisAgreeCol.includes(col)) {
        //agree
        td.innerHTML = value === true ? 'Yes' : '-';
      }
      if (dollarColumn.includes(col)) {
        //DOLLAR_COL
        td.innerHTML = value ? `${value}` : null; //$
      }
    }
  }

  function updateSheetData(nextSheetData, row, rowIndex) {
    if (rowIndex != null) {
      nextSheetData[rowIndex] = {
        ...nextSheetData[rowIndex],
        ...row,
        row_id: null,
        file_name: currentUpload.upload_id,
        chart_id: currentUpload.id,
      };
      if (!nextSheetData[rowIndex]['rendering']) {
        nextSheetData[rowIndex]['rendering'] = currentUpload.provider?.full_name;
      }
    } else {
      nextSheetData.push({ 
        ...row, 
        id: null,
        file_name: currentUpload.upload_id,
        chart_id: currentUpload.id,
       });
    }
    return nextSheetData;
  }

  const checkDynamicColumnsEmpty = (
    currentSheetData, 
    columnNamePrefx,
    rowIndex
  ) => {
    for (let key in currentSheetData[rowIndex]) {
      if (key.startsWith(columnNamePrefx)) {
        if (currentSheetData[rowIndex][key]) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * 
   * @param {contains a list of new Industry codes that needs to be added} newDataSplits 
   * @param {base Column Name (billed_icd)} baseColumnName 
   * @param {billed_icd_{count}} columnName 
   * @param {sheet data} nextSheetData 
   * @param {*} rowIndex 
   * @param {*} isNewRow 
   * @returns 
   */
  const separateCommaSeparatedIndustryCodes = (
    newDataSplits, 
    baseColumnName, 
    columnName, 
    nextSheetData, 
    rowIndex, 
    isNewRow
  ) => {
    let row = {}
    const colNameParts = columnName.split('_');
    
    let startIdx = 1;
    if (colNameParts.length > 2) {
      startIdx = parseInt(colNameParts[colNameParts.length - 1]);
    }
    let allCodes = [...newDataSplits];

    if (!isNewRow) {
      for (let idx = startIdx + 1;; idx++) {
        let dynamicColumnName =  `${baseColumnName}_${idx}`;
        if (dynamicColumnName in nextSheetData[rowIndex]) {
          allCodes.push(nextSheetData[rowIndex][dynamicColumnName]);
        } else {
          break;
        }
      }
    }
    
    let k = 0, idx = startIdx;
    for (let i = allCodes.length - 1; i >= 0; i--) {
      if (allCodes[i] === null) {
        allCodes.pop();
      } else {
        break;
      }
    }
    for (; k < allCodes.length; idx++) {
      let dynamicColumnName =  baseColumnName;
      if (idx > 1) {
        dynamicColumnName =  `${baseColumnName}_${idx}`;
      }
      row[dynamicColumnName] = allCodes[k++] || null;
    }
    row[`${baseColumnName}_${idx}`] = null;
    
    nextSheetData = updateSheetData(
      nextSheetData,
      {...row},
      isNewRow ? null : rowIndex
    );

    return nextSheetData;
  }

  const onBeforeHotChange = change => {
    let nextSheetData = [...sheetData];
    let addedColumns = [];
    change.map(changes => {
      const rowIndex = changes[0];
      const columnName = changes[1];
      const oldValue = changes[2];
      const newValue = changes[3];

      if (oldValue === newValue || (!oldValue && !newValue)) {
        return;
      }
      const baseColumnName = getBaseKeyword(columnName, dynamicColumnKeywords);

      if (newValue !== "") {
        const validationResult = validateColumnValue(columnName, newValue);
        if (!validationResult.isValid) {
          toast.showWarning({ description: validationResult.message });
          return;
        }
      }
      
      // Keep pasted cells at their visual row, including spare rows below the data.
      while (nextSheetData.length < rowIndex) nextSheetData.push({});
      const isNewRow = rowIndex >= nextSheetData.length;
      switch (baseColumnName) {
        case AUDIT_COLUMN_HEADER.SRVCS_NO:
        case AUDIT_COLUMN_HEADER.AUDITED_CPT:
          //CPT Format:- CPT-Modifier-..*Units 
          if (!newValue || newValue === " ") {
            const row = {
              [columnName]: null,
            };
            nextSheetData = updateSheetData(
              nextSheetData,
              row,
              isNewRow ? null : rowIndex
            );
            nextSheetData = updateRVUand$Value(nextSheetData, rowIndex, industryCodes)
            iaddState.on();
          } else {
            const newDataSplits = newValue?.split(",").map(cpt => cpt.trim());

            try {
              if (!Object.keys(industryCodes).length) {
                throw new Error("Industry codes are not available");
              } 
              validateCPTList(newDataSplits, INDUSTRY_CODES, rowIndex)
              validateUnitsList(newDataSplits)
              nextSheetData = separateCommaSeparatedIndustryCodes(
                newDataSplits,
                baseColumnName,
                columnName,
                nextSheetData,
                rowIndex,
                isNewRow
              );
              nextSheetData = updateRVUand$Value(nextSheetData, rowIndex, industryCodes)
              ipddState.on();
              
            } catch (err) {
              toast.showError({
                description: err.message || "CPT is not available or please check the format CPT*UNITS"
              });
              const row = {
                [columnName]: null,
              };
              nextSheetData = updateSheetData(
                nextSheetData,
                row,
                isNewRow ? null : rowIndex
              );
              nextSheetData = updateRVUand$Value(nextSheetData, rowIndex, industryCodes)
            }
          }

          break;
        case AUDIT_COLUMN_HEADER.BILLED_ICD:
        case AUDIT_COLUMN_HEADER.AUDITED_ICD:
          if (!newValue || newValue === " ") {
            const row = {
              [columnName]: null,
            };
            nextSheetData = updateSheetData(
              nextSheetData,
              row,
              isNewRow ? null : rowIndex
            );
          }else {
            let newDataSplits = newValue?.split(",").map(icd => icd.trim());
            try {
              if (!Object.keys(iCDCodes).length) {
                throw new Error("ICD codes are not available");
              }
              newDataSplits = validateICDList(newDataSplits, ICD_CODES, rowIndex)
              nextSheetData = separateCommaSeparatedIndustryCodes(
                newDataSplits,
                baseColumnName,
                columnName,
                nextSheetData,
                rowIndex,
                isNewRow
              );
            } catch (err) {
              toast.showError({
                description: err.message || `ICD is not available`,
              });
              const row = {
                [columnName]: null,
              };
              nextSheetData = updateSheetData(
                nextSheetData,
                row,
                isNewRow ? null : rowIndex
              );
            }
          }
          break;
        case AUDIT_COLUMN_HEADER.BILLED_MODIFIER:
        case AUDIT_COLUMN_HEADER.AUDITED_MODIFIER:
          if(newValue){
            nextSheetData = updateSheetData(
              nextSheetData,
              {
                [columnName]: newValue === '' ? null : newValue.toUpperCase(),
              },
              isNewRow ? null : rowIndex
            );
            nextSheetData = updateRVUand$Value(nextSheetData, rowIndex, industryCodes)
          }else{
            nextSheetData = updateSheetData(
              nextSheetData,
              {
                [columnName]: null,
              },
              isNewRow ? null : rowIndex
            );
            nextSheetData = updateRVUand$Value(nextSheetData, rowIndex, industryCodes)
          }
          break;
        case AUDIT_COLUMN_HEADER.AGREE: 
        case AUDIT_COLUMN_HEADER.DISAGREE: {
          const editRow = handleAgreeDisagree(nextSheetData, columnName, columnName === "agree" ? "disagree" : "agree", newValue);
          nextSheetData = updateSheetData(
            nextSheetData,
            editRow,
            isNewRow ? null : changes[0]
          );
          if (checkDynamicColumnsEmpty(nextSheetData, "audited_cpt", rowIndex) && editRow?.agree) {
            nextSheetData[rowIndex].audited_code =
              nextSheetData[rowIndex].srvcs_no;
            nextSheetData[rowIndex].audited_rvu =
              nextSheetData[rowIndex].provider_rvu;
            nextSheetData[rowIndex].audited_dollar_value =
              nextSheetData[rowIndex].provider_dollar_value;
            nextSheetData = addColumnOnAgreeDisagree("srvcs_no", "audited_cpt", nextSheetData, rowIndex);
            nextSheetData = addColumnOnAgreeDisagree("billed_modifier", "audited_modifier", nextSheetData, rowIndex);
          }

          break;
        }
        case AUDIT_COLUMN_HEADER.ICD_AGREE: 
        case AUDIT_COLUMN_HEADER.ICD_DISAGREE: {
          const editRow = handleAgreeDisagree(nextSheetData, columnName, columnName === "icd_agree" ? "icd_disagree" : "icd_agree", newValue);
          nextSheetData = updateSheetData(
            nextSheetData,
            editRow,
            isNewRow ? null : changes[0]
          );
          if (checkDynamicColumnsEmpty(nextSheetData, "audited_icd", rowIndex) && editRow.icd_agree) {
            nextSheetData = addColumnOnAgreeDisagree("billed_icd", "audited_icd", nextSheetData, rowIndex);
          }

          break;
        }

        case AUDIT_COLUMN_HEADER.ENC_DT:
          let data = changes[3];
          if (data) {
            const d = Date.parse(data);
            if (d) {
              const encDtRow1 = {
                [changes[1]]: format(new Date(d), 'MM-dd-yyyy'),
              };
              nextSheetData = updateSheetData(
                nextSheetData,
                encDtRow1,
                isNewRow ? null : changes[0]
              );
            } else {
              toast.showWarning({ description: 'Invalid Date: Use MM-DD-YY/YYYY format' });
            }
          } else {
            const encDtRow1 = {
              [changes[1]]: null,
            };
            nextSheetData = updateSheetData(
              nextSheetData,
              encDtRow1,
              isNewRow ? null : changes[0]
            );
          }

          break;
        case AUDIT_COLUMN_HEADER.RENDERING:
          let obj = null;
          if (changes[3]) {
            obj = providerOptions.find(
              p => p.value.toLowerCase() === changes[3].toLowerCase()
            );
          }
          let renderingData = {
            [changes[1]]: null,
          };
          if (obj) {
            renderingData = {
              [changes[1]]: changes[3],
            };
          } else {
            toast.showError({
              description: 'This Rendering value is not available.',
            });
          }
          nextSheetData = updateSheetData(
            nextSheetData,
            renderingData,
            isNewRow ? null : changes[0]
          );

          break;
        case 'response':
          nextSheetData = updateSheetData(
            nextSheetData,
            { [columnName]: newValue === '' ? null : newValue },
            isNewRow ? null : rowIndex
          );
          break;
        default:
          const defaultRow = {
            [changes[1]]: changes[3] === '' ? null : changes[3],
          };
          nextSheetData = updateSheetData(
            nextSheetData,
            defaultRow,
            isNewRow ? null : changes[0]
          );

          break;
      }
    });

    const _change = change[0];
    const [rowIndex, columnName, oldValue, newValue] = _change;

    addedColumns.push((addColumn(columnName, nextSheetData, newValue, true) ?? {}).data);
    // const hasAddedColumn = addColumn(columnName, nextSheetData, newValue, true);

    const updateDictionary = Object.fromEntries(
      addedColumns.filter(item => !!item).map(item => [item, null])
    )

    if (true) {
      nextSheetData = nextSheetData.map(nsd => {
        if (nsd.id) {
          return {
            ...nsd,
            // [hasAddedColumn.data]: null,
            ...updateDictionary
          };
        } else {
          return nsd;
        }
      });
    }

    setLocalSheetData(nextSheetData);
    updateSheet(nextSheetData);
  
    const uniqueRowIndices = [...new Set(change.map(([rowIndex]) => rowIndex))];
    // Multi-row edits are saved immediately instead of waiting for row change or idle timer
    const isMultiRowEdit = uniqueRowIndices.length > 1;

    if (!view && !auditSheetContext.isAuditFormOpen) {
      if (isMultiRowEdit) {
        const newPending = {
          created: new Set(pendingChangesRef.current.created),
          updated: new Map(pendingChangesRef.current.updated),
          deleted: new Set(pendingChangesRef.current.deleted),
        };

        change.forEach(([rowIndex, , oldValue, newValue]) => {
          if (hasValueChanged(oldValue, newValue)) {
            const updatedRowData = nextSheetData[rowIndex];
            if (updatedRowData) {
              if (!updatedRowData.id || updatedRowData.id === null) {
                queueCreatedRow(newPending.created, updatedRowData);
                setPendingNewRows(prev => new Set([...prev, rowIndex]));
              } else {
                newPending.updated.set(updatedRowData.id, updatedRowData);
              }
            }
          }
        });

        pendingChangesRef.current = newPending; // update ref synchronously first
        setPendingChanges(newPending);           // then sync state for UI
        performSave();                           // ref is populated, safe to save now

      } else {
        const [rowIndex] = change[0];
        
        if (change.some(([, , previous, next]) => hasValueChanged(previous, next))) {
          const updatedRowData = nextSheetData[rowIndex];
          trackChange(updatedRowData);
          if (!updatedRowData?.id || updatedRowData.id === null) {
            setPendingNewRows(prev => new Set([...prev, rowIndex]));
          }
          handleRowChange(rowIndex, false);
        }
      }
    }
    return false;
  };

  function hasComment(row, col) {
    if (row < sheetData?.length) {
      const colData = batch
        ? auditSheetColumnsKeys[col]
        : auditSheetColumnsKeys[col + 1];
      const rowId = sheetData[row]?.id;
      if (rowId && commentFlags && Object.keys(commentFlags).length) {
        if (commentFlags[rowId]?.[colData]) {
          return true;
        }
      }
    }
    return false;
  }

  function columnHeaders() {
    // auditSheetHeaders
    return auditSheetHeaders.map((header, i) => {
      const fontSize = getScaledHeaderFontSize(scale);
      return `<div style="font-size: ${fontSize};"class="header">${header}</div>`;
    });
  }

  // context options
  function contextOption() {
    return {
      async callback(key, selection, clickEvent) {
        // Common callback for all options
        onCommentClick(
          selection,
          { pageX: clickEvent.screenX, pageY: clickEvent.screenY },
          key === 'comments',
          false
        );

        if (key === 'comments') {
          const { row, column } = getRowColArray(selection[0], sheet, batch);
          await getComments(uploadId, {
            row,
            column,
          });
        }
      },
      items: {

        enable_missing_info: {
          name: '<span style="background-color: #ffe6e6; padding: 1px; border-radius: 3px;">Enable Missing Info</span>',
          callback: async function (key, selection) {
            let nextSheetData = [...sheetData];
            const temp = getRowColIndex(selection[0], sheet);
            const row = temp.rows[0];

            if (row >= 0 && row < nextSheetData.length) {
              if (nextSheetData[row].missing_info === null || nextSheetData[row].id === null) {
                nextSheetData[row].missing_info = true;
                if (nextSheetData[row].id === null) {
                  nextSheetData = updateSheetData(nextSheetData, {}, row)
                }
                await updateAuditSheet({ ...sheet, data: nextSheetData });
              } else {
                console.log("Missing Info is already true or row is empty.");
              }
            } else {
              console.log("Invalid row index.");
            }
          },
          hidden() {
            const selectedIndex = this.getSelectedLast()[0];
            return (
              selectedIndex < 0 ||
              selectedIndex >= sheetData.length ||
              sheetData[selectedIndex].missing_info === true
            );
          },
        },

        disable_missing_info: {
          name: '<span style="background-color: #e6ffe6; padding: 1px;  border-radius: 3px;">Disable Missing Info</span>',
          callback: async function (key, selection) {
            let nextSheetData = [...sheetData];
            const temp = getRowColIndex(selection[0], sheet);
            const row = temp.rows[0]
            if (row >= 0 && row < nextSheetData.length) {
              if (nextSheetData[row].missing_info !== null) {
                nextSheetData[row].missing_info = null;
                await updateAuditSheet();
              } else {
                console.log("Missing Info is already null or row is empty.");
              }
            } else {
              console.log("Invalid row index.");
            }
          },
          hidden() {
            const selectedIndex = this.getSelectedLast()[0];
            return (
              selectedIndex < 0 ||
              selectedIndex >= sheetData.length ||
              // sheetData[row].missing_info === null
              sheetData[selectedIndex].missing_info === null ||
              sheetData[selectedIndex].id === null
            );
          },
        },

        row_above: {
          name: () => '<p>Insert row above</p>',
          disabled() {
            return view;
          },
        },

        row_below: {
          disabled() {
            return view;
          },
        },

        remove_row: {
          callback: async function (key, selection) {
            const { rows } = getRowColIndex(selection[0], sheet);
            
            const idsToDelete = rows
              .map(rowIdx => {
                const hot = hotRef.current.hotInstance;
                const rowData = hot.getSourceDataAtRow(rowIdx);
                return rowData?.id;
              })
              .filter(id => id !== null && id !== undefined);
            
            trackDeletion(idsToDelete);
            
            rows.forEach(rowIdx => {
              const hot = hotRef.current.hotInstance;
              const rowData = hot.getSourceDataAtRow(rowIdx);
              if (rowData && (!rowData.id || rowData.id === null)) {
                const next = { ...pendingChangesRef.current, created: new Set(pendingChangesRef.current.created) };
                for (const queued of next.created) {
                  if (queued._auditLocalId === rowData._auditLocalId) next.created.delete(queued);
                }
                pendingChangesRef.current = next;
                setPendingChanges(next);
              }
            });
            
            sheetRef.current = { ...sheetRef.current, data: sheetRef.current.data.filter((_, index) => !rows.includes(index)) };
            removeRows(rows);
            setPendingNewRows(prev => {
              const next = new Set(prev);
              rows.forEach(r => next.delete(r));
              return next;
            });
            
            if (!view && !auditSheetContext.isAuditFormOpen) {
              debouncedSave();
            }
          },
          disabled() {
            return view;
          },
        },

        cut: {
          disabled() {
            return view;
          },
        },

        copy: {
          disabled() {
            return view;
          },
        },

        freeze_column: {
          name: "Freeze Column",
          disabled() {
            return !view;
          },
        },

        unfreeze_column: {
          name: "Unfreeze Column",
          disabled() {
            return !view;
          },
        },

        export_to_csv_cpt: {
          name: "Export to CSV (CPT-wise)",
          callback(key, selection, clickEvent) {
            const hot = hotRef.current.hotInstance;
    
            // Helper to escape CSV fields
            const escapeCsvField = (field) => {
                if (field === null || field === undefined) {
                    return '';
                }
                const stringField = String(field);
                if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\r\n')) {
                    const escapedField = stringField.replace(/"/g, '""');
                    return `"${escapedField}"`;
                }
                return stringField;
            };
    
            const sourceData = hot.getSourceData();

            /**
            const columnOrderProvided = [
            'file_id',
            'encounter',
            'rendering',
            'enc dt',
            'cpt',
            'modifier',
            'units',
            'ICDs',
            'Notes',
            'Response',
            'Provider Education',
            'Number & complexity',
            'Amount and complexity',
            'Mortality of Patient Management'
            ];
            */
            
            const OMITTING_KEYS = [
              'missing_info',
              'provider_rvu',
              'provider_dollar_value',
              'agree',
              'disagree',
              'audited_rvu',
              'audited_dollar_value',
              'icd_agree',
              'icd_disagree'
            ]
            const FLATTENING_KEYS = [
              'srvcs_no',
              'audited_cpt',
              'billed_modifier',
              'audited_modifier'
            ]
            

            const columnOrder = columnData.map(c => c.data);
            // these are the keys before the cpt
            const STARTING_KEYS = [
              'file_name',
              'encounter_no',
              'rendering',
              'enc_dt'
            ]
            let last_audited_icd = -1;
            let omittingIndices =[], firstFlatteningIndex = -1;
            columnOrder.forEach((val, idx) => {
              if (OMITTING_KEYS.includes(val)) {
                omittingIndices.push(idx);
                return;
              }
              for (const prefix of FLATTENING_KEYS) {
                if (val.startsWith(prefix)) {
                  omittingIndices.push(idx);
                }
              }
            });

            let filteredColumnOrder = columnOrder.filter((_, idx) => !omittingIndices.includes(idx))
            let notes_pos = -1;
            filteredColumnOrder.forEach((val, idx) => {
              if (STARTING_KEYS.includes(val)) {
                firstFlatteningIndex = idx;
              }
              if (val.startsWith("audited_icd")) {
                last_audited_icd = idx;
              }
              if (idx !== -1 && val === 'notes') {
                notes_pos = idx;
              }
            })

            firstFlatteningIndex += 1;
            
            if (last_audited_icd != -1 && notes_pos !== -1) {
              // placing the notes after the last audited icd
              filteredColumnOrder.splice(last_audited_icd + 1, 0, "notes");
              // removing the notes key from it's original position
              filteredColumnOrder.splice(notes_pos, 1);
            }
            const filteredCSVHeaders = auditSheetHeaders.filter((_, idx) => !omittingIndices.includes(idx));
            if(last_audited_icd != -1 && notes_pos !== -1) {
              // placing the Notes header after the last audited icd
              filteredCSVHeaders.splice(last_audited_icd + 1, 0, "Notes");
              // removing the old notes position in the header list
              filteredCSVHeaders.splice(notes_pos, 1);
            }

            const FLATTENED_CSV_HEADERS = ["Billed CPT", "Audited CPT", "Billed Modifier", "Audited Modifier", "Units Billed", "Units Reviewed"];
            filteredCSVHeaders.splice(firstFlatteningIndex, 0, ...FLATTENED_CSV_HEADERS)

            const processedData = sourceData.map(row => {
                const newRow = { ...row };
                if (newRow.id === null) {
                    return null; 
                }

                const responseValue = newRow.response;
                if (responseValue) {
                    newRow.response = decodeResponseValue(responseValue, row, responseAuditIssues);
                }
                return newRow;
            }).filter(Boolean); 
            
            

            
            const csvHeader = filteredCSVHeaders.map(escapeCsvField).join(',');

            const csvRows = processedData.map(row => {
              if (typeof(row) !== 'object' || row === null) {
                return '';
              }

              const createCptDict = (key, keyPrefix, dict) => {
                if (!key.startsWith(keyPrefix)) {
                  return false;
                }
                const keySplits = key.split("_")
                const cptId = parseInt(keySplits.length === 3 ? keySplits[2] : "1", 10);
                dict[cptId] = row[key];
                return true;
              }

              let flatteningKeyValue = Array.from({ length: FLATTENING_KEYS.length }, (_, i) => ({}));

              Object.keys(row).forEach(key => {
                for (const [idx, prefix] of FLATTENING_KEYS.entries()) {
                  if (createCptDict(key, prefix, flatteningKeyValue[idx])) {
                    break;
                  }
                }
                
              });

              const getCptUnits = code => {
                if (!code) {
                  return [undefined, undefined];
                }
                const cptSplits = code.split("*");
                return cptSplits.length > 1 ? [cptSplits[0], cptSplits[1]] : [cptSplits[0], 1];
              }

              const maxCPTs = Math.max(...Object.keys(flatteningKeyValue[0]), ...Object.keys(flatteningKeyValue[1]));
              let flatteningValueList = [];
              for (let idx = 1; idx <= maxCPTs; idx++) {
                if (!flatteningKeyValue[0][idx] && !flatteningKeyValue[1][idx]) {
                  continue;
                }
                
                // pushing a list of ["Billed CPT", "Audited CPT", "Billed Modifier", "Audited Modifier", "Units Billed", "Units Reviewed"]
                let units_list = []
                flatteningValueList.push(FLATTENING_KEYS.map((_, i) => {
                  if (i === 0 || i == 1) {
                    const ret = getCptUnits(flatteningKeyValue[i][idx]);
                    units_list.push(ret[1]);
                    return ret[0];
                   }
                   let ret_list = [flatteningKeyValue[i][idx]];
                   if (i === 3) {
                    ret_list.push(...units_list)
                   }
                  return ret_list
                }).flat().map(escapeCsvField))
              }

              // parse the notes
              let commonNotes = "";
              let CPTtoNotesMap = {}


              flatteningValueList.forEach(el => {
                if (el[0]) {
                  CPTtoNotesMap[el[0]] = "";
                }
              });
              const NOTES_DELIMETERS = ['.', ';']
              if (row.notes) {
                for (const note of row.notes.split(new RegExp(`[${NOTES_DELIMETERS.join('')}]`))) {
                  if (!note) {
                    continue;
                  }
                  const tokens = note.split(" ");
                  let matchedCPTs = new Set();
                  for (const token of tokens) {
                    if (token in CPTtoNotesMap) {
                      matchedCPTs.add(token);
                    }
                  }

                  for (const cpt of matchedCPTs) {
                    CPTtoNotesMap[cpt] += `${note}. `;
                  }
                  if (!matchedCPTs.size) {
                    commonNotes += `${note}. `;
                  }
                }
              }
              
              // note parsing ends

              let result = [];

              for (const flattenedItem of flatteningValueList) {
                result.push(filteredColumnOrder.map((key, idx) => {
                  let flatteningPrefix = [];
                  if (idx === firstFlatteningIndex) {
                    flatteningPrefix = flattenedItem;
                  }
                  let ret; 
                  if (key === "enc_dt") {
                    ret = `="${escapeCsvField(row['enc_dt'])}"`;
                  }
                  else if (key === "notes") {
                    if (flattenedItem[0]) {
                      ret = escapeCsvField(CPTtoNotesMap[flattenedItem[0]] + commonNotes);
                    } else {
                      ret = escapeCsvField(commonNotes);
                    }
                  }
                  else {
                    ret = escapeCsvField(row[key]);
                  }
                  return [...flatteningPrefix, ret];
                }).flat().join(','))
              }
                
              return result;
            }).flat();
    
            const csvString = [csvHeader, ...csvRows].join('\r\n');
    
            const blob = new Blob([`\uFEFF${csvString}`], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            if (link.download !== undefined) { 
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `audit_export.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
          },
        },
        export_to_csv: {
          name: "Export to CSV",
          callback(key, selection, clickEvent) {
              const hot = hotRef.current.hotInstance;
      
              // Helper to escape CSV fields
              const escapeCsvField = (field) => {
                  if (field === null || field === undefined) {
                      return '';
                  }
                  const stringField = String(field);
                  if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\r\n')) {
                      const escapedField = stringField.replace(/"/g, '""');
                      return `"${escapedField}"`;
                  }
                  return stringField;
              };
      
              const sourceData = hot.getSourceData();
              const headers = auditSheetHeaders;
              const columnOrder = columnData.map(c => c.data);
      
              const processedData = sourceData.map(row => {
                  const newRow = { ...row };
                  if (newRow.id === null) {
                      return null; 
                  }
                  const responseValue = newRow.response;
                  if (responseValue) {
                      newRow.response = decodeResponseValue(responseValue, row, responseAuditIssues);
                  }
                  return newRow;
              }).filter(Boolean); 
      
              const csvHeader = headers.map(escapeCsvField).join(',');
              const csvRows = processedData.map(row => {
                  return columnOrder.map(key => {
                      const value = (typeof row === 'object' && row !== null) ? row[key] : '';
                      return escapeCsvField(value);
                  }).join(',');
              });
      
              const csvString = [csvHeader, ...csvRows].join('\r\n');
      
              const blob = new Blob([`\uFEFF${csvString}`], { type: 'text/csv;charset=utf-8;' });
              const link = document.createElement('a');
              if (link.download !== undefined) { 
                  const url = URL.createObjectURL(blob);
                  link.setAttribute('href', url);
                  link.setAttribute('download', `${currentUpload.upload_id}.csv`);
                  link.style.visibility = 'hidden';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
              }
            },
        },

        rebuttals: {
          name: 'View Rebuttal',
          callback: async function(key, selection) {
            const rowIndex = selection[0].start.row;
            if (rowIndex >= 0 && rowIndex < sheetData.length) {
              await onCommentClick(rowIndex, sheetData[rowIndex]);
            }
          },
          hidden() {
            const selectedIndex = this.getSelectedLast()[0];
            return !sheetData[selectedIndex]?.id;
          },
        },
      },
    };
  }

  // onKeyPress
  function onKeyPress(e) {
    // column number
    let col = this.getSelectedLast()[1];

    var evt = e || window.event; // IE support
    var key = evt.charCode || evt.keyCode || 0;

    // check for cut and paste
    var isClipboard = false;
    var ctrlDown = evt.ctrlKey || evt.metaKey; // Mac support

    // Check for Alt+Gr (http://en.wikipedia.org/wiki/AltGr_key)
    if (ctrlDown && evt.altKey) isClipboard = false;
    // Check for ctrl+c, v and x
    else if (ctrlDown && key === 67) isClipboard = true; // c
    else if (ctrlDown && key === 86) isClipboard = true; // v
    else if (ctrlDown && key === 88) isClipboard = true; // x
    else if (ctrlDown && evt.key === 'z') isClipboard = true;
    const isNumeric =
      NUMERIC_KEYS.includes(key) ||
      (key >= 35 && key <= 40) ||
      (key >= 48 && key <= 57) ||
      (key >= 96 && key <= 105);

    switch (col) {
      case 3:
        // Shift movement and copy-paste are not enabled for column 3 (Enc Date).
        if ((!isNumeric && !isClipboard) || e.shiftKey) {
          // prevent alpha characters
          e.stopImmediatePropagation();
          e.preventDefault();
        }
        break;
      default:
        break;
    }

    numericColumns.forEach(nc => {
      switch (col) {
        case nc:
          if ((!isNumeric && !isClipboard) || e.shiftKey) {
            // prevent alpha characters
            e.stopImmediatePropagation();
            e.preventDefault();
          }
          break;
        default:
          break;
      }
    });

    // save when Enter, up or down moves to next row
    if (e.keyCode === 13 || e.keyCode === 38 || e.keyCode === 40) {
      setTimeout(() => {
        handleRowChange(this.getSelectedLast()[0], true);
      }, 0);
    }
  }

  // onMouseClick on cell
  function _afterOnCellMouseDown(e, p) {
    // e - event , p - cellCords[ row , column ]
    closeComment();
    setSelectedRowCol({ row: p.row, col: p.col });
    handleRowChange(p.row, true);
    if (p.row >= 0 && p.row < sheetData.length) {
      onSelectionEnd?.(sheetData[p.row]);
    }
  }

  function cellProps(row, col) {
    var cellProperties = {};

    const COL = highlightedColumn ?? [6, 14];
    if (COL.includes(col)) {
      cellProperties.renderer = columnHighlight; // uses lookup map
    }

    if (hasComment(row, col)) {
      cellProperties.renderer = commentCell; // uses comment map
    }

    // Check for missing_info and apply background color
    const rowData = this.instance.getSourceDataAtRow(row);
    if (rowData && rowData.missing_info) {
      cellProperties.className = 'missing-info';
    }

    // Row-level rebuttal highlighting
    if (rowData?.id && commentFlags[rowData.id]) {
      const rowFlag = commentFlags[rowData.id]['__row__'];
      if (rowFlag === true) {
        cellProperties.className = ((cellProperties.className || '') + ' cq-row-rebuttal-open').trim();
      } else if (rowFlag === false) {
        cellProperties.className = ((cellProperties.className || '') + ' cq-row-rebuttal-resolved').trim();
      }
    }

    const currentPending = pendingChangesRef.current;
    const isCurrentlySaving = isSavingRef.current;
    
    if (rowData) {
      let cellState = null;
      
      if (isCurrentlySaving) {
        if (!rowData.id || rowData.id === null) {
          if (pendingNewRows.has(row)) {
            cellState = 'saving';
          }
        } else {
          if (currentPending.updated.has(rowData.id)) {
            cellState = 'saving';
          }
        }
      } else {
        if (!rowData.id || rowData.id === null) {
          if (pendingNewRows.has(row)) {
            cellState = 'editing';
          }
        } else {
          if (currentPending.updated.has(rowData.id)) {
            cellState = 'editing';
          }
        }
      }
      
      if (cellState) {
        if (!cellProperties.className) {
          cellProperties.className = '';
        }
        cellProperties.className += ` cell-${cellState}`;
      }
    }

    if(industryCodes){
      cellProperties.CPT = industryCodes;
    }
    if(iCDCodes){
      cellProperties.ICD = iCDCodes;
    }

    if(modifiers){
      cellProperties.Modifiers = modifiers;
    }

    return cellProperties;
  }

  function onPaste(data, coords) {
    const [startRow, startCol] = [coords[0].startRow, coords[0].startCol];
    const actualData = data[0];

    const rvuIndices = columnData.findIndex(col => col.data === 'provider_rvu');

    // Check if the destination column is 'enc_dt'
    if (startCol === rvuIndices) {
      // Check if any of the pasted values is not a number
      if (actualData.some(value => isNaN(+value))) {
        return false;
      }
    }

    // Continue with the paste operation for other columns or valid numeric values
    return true;
  }

  // table settings
  /** @type {import('handsontable/settings').GridSettings} */

  const hotSettings = {
    //sheet data
    data: sheetData,

    height: height,

    // sheet column header
    colHeaders: columnHeaders(),

    //  right-click or context menu
    contextMenu: contextOption(),

    minSpareRows: 10,

    licenseKey: 'non-commercial-and-evaluation',

    //beforeChange when onChange returns [ columnIndex, columnName,oldValue, newValue ]
    beforeChange: onBeforeHotChange,

    beforeKeyDown: onKeyPress,

    outsideClickDeselects: false,

    afterOnCellMouseDown: _afterOnCellMouseDown,

    afterSelectionEnd: (row) => {
      if (row >= 0 && row < sheetData.length) {
        onSelectionEnd?.(sheetData[row]);
      }
    },

    cells: cellProps,

    // onpaste

    beforePaste: onPaste,

    // hide columns
    // hiddenColumns:{
    //   columns:[0]
    // }
  };

  const hotTableDefaultSettings = {
    ...hotSettings,
    rowHeights: getScaledRowHeight(scale),
    stretchH: 'all',
    autoColumnSize: false,
    autoRowSize: false,
    wordWrap: true,
  };

  function addColumnOnAgreeDisagree(billedFieldName, auditFieldName, nextSheetData, rowIndex, isAgree=true){
    //function for adding or removing column and value when we Agree or disagree
    let isAdded = false;
    let hasAddedColumn = null;
    for (let key in nextSheetData[rowIndex]) {
      // Check if the key starts with "srvcs_no"
      if(isAgree){
        isAdded = false;
        hasAddedColumn = null;
        if (key.startsWith(billedFieldName)) {
          if(key === billedFieldName){
            nextSheetData[rowIndex][auditFieldName] = nextSheetData[rowIndex][billedFieldName];
            
            let nextCloumnName = createColumnDataName(key);
            if(nextSheetData[rowIndex].hasOwnProperty(nextCloumnName)){
              hasAddedColumn = addColumn(auditFieldName, nextSheetData);
              isAdded = hasAddedColumn && Object.keys(hasAddedColumn).length;
            }
          }
          else{
            const srvcs_parts = key.split('_');
            nextSheetData[rowIndex][auditFieldName+"_"+srvcs_parts[2]] = nextSheetData[rowIndex][key];

            let nextCloumnName = createColumnDataName(key);
            if(nextSheetData[rowIndex].hasOwnProperty(nextCloumnName)){
              hasAddedColumn = addColumn(auditFieldName+"_"+srvcs_parts[2], nextSheetData);
              isAdded = hasAddedColumn && Object.keys(hasAddedColumn).length;
            }
          }
          if (isAdded) {
            nextSheetData = nextSheetData.map(nsd => {
              if (nsd.id) {
                return {
                  ...nsd,
                  [hasAddedColumn.data]: null,
                };
              } else {
                return nsd;
              }
            });
          }
        }
      }else{
        if (key.startsWith(auditFieldName)) {
          nextSheetData[rowIndex][key] = null;
        }
      }
    }
    return nextSheetData;
  }

  function addColumn(columnName, nextSheetData, newValue=null, checkhasValue=false) {
    // const [row, column, oldValue, newValue] = changes[0];
    const column = columnName
    let haveValue = false;
    if (newValue) {
      haveValue = true;
    }

    // const [row, column, oldValue, newValue] = changes[0];

    const _selectedIndex = columnData.findIndex(cd => cd.data === column);

    // const [rowIndex, selectedColumnIndex] =
    //   hotRef.current?.hotInstance.getSelected()[0];

    const selectedColumnIndex = _selectedIndex;

    
    const newColumnHeader = auditSheetHeaders[_selectedIndex];
    
    const _newColumnHeader = creatColumnHeader(newColumnHeader);
    
    const newColumnName = createColumnDataName(column);
    
    const isEmptyColumn = hotRef.current?.hotInstance?.isEmptyCol(
      newColumnName
    );
    const newColumnData = {
      data: newColumnName,
      type: 'text',
      width: windowSize * 0.15,
      readOnly: view,
      renderer: 'customStylesRenderer',
    };

    let existingHeaders = auditSheetHeaders;
    let existingColumnData = columnData;

    const colAlreadyExist = nextSheetData.find(cd => {
      const hasAreadyxist = cd.hasOwnProperty(newColumnName);
      if (hasAreadyxist && cd.id) {
        return cd;
      }
    });

    function _addColumn() {
      const canAddColumn = isColumnMatched(column, dynamicColumnKeywords);

      if (canAddColumn) {
        existingHeaders.splice(selectedColumnIndex + 1, 0, _newColumnHeader);

        existingColumnData.splice(selectedColumnIndex + 1, 0, newColumnData);

        setLocalHeader(existingHeaders);
        setLocalColumnData(existingColumnData);

        return newColumnData;
      }

      return null;
    }

    if(checkhasValue){
      if (isEmptyColumn && haveValue && !colAlreadyExist) {
        return _addColumn();
      }
    }else{
      if (isEmptyColumn && !colAlreadyExist) {
        return _addColumn();
      }
    }
  }

  return (
    <HotTable
      ref={hotRef}
      columns={columnData}
      // colHeaders={true}
      data={sheetData} //arrangeData(dummySheetData)
      // undo={true}
      settings={{ ...hotTableDefaultSettings, 
        manualColumnResize: true,
        rowHeaders: true,
        manualColumnFreeze: true, }}
    />
  );
};
export default AuditSheet;
